import { Component, OnInit } from "@angular/core";
import { DatePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { StatusService } from "./status.service";
import { HiddenItemsService } from "./hidden-items.service";

import { MatIconModule } from "@angular/material/icon";
import { ServiceStatusComponent } from "../service-status/service-status.component";
import { PorscheDesignSystemModule } from "@porsche-design-system/components-angular";
import { MatTab, MatTabGroup } from "@angular/material/tabs";
import { MatTooltip } from "@angular/material/tooltip";
import { DashboardResult } from "../model/dashboard-result";
import { ServiceOverview } from "../model/service-overview";
import { LambdaOverview } from "../model/lambda-overview";
import { FrontendOverview } from "../model/frontend-overview";

const SHOW_STACK_NAME_KEY = "sm_show_stack_name";

@Component({
  selector: "app-services",
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatIconModule,
    ServiceStatusComponent,
    PorscheDesignSystemModule,
    MatTabGroup,
    MatTab,
    MatTooltip,
    DatePipe
  ],
  templateUrl: "./services.component.html",
  styleUrl: "./services.component.scss"
})
export class ServicesComponent implements OnInit {
  public dashboardResult: DashboardResult = Object.assign({});
  public showTitle: boolean = false;
  public loading: boolean = true;

  public servicesOpen: boolean = true;
  public lambdasOpen: boolean = true;
  public frontendsOpen: boolean = true;
  public settingsOpen: boolean = false;
  public showStackName: boolean = false;

  public searchTerm: string = "";

  constructor(
    private healthService: StatusService,
    public hiddenItems: HiddenItemsService
  ) {}

  ngOnInit(): void {
    this.showStackName = this.loadShowStackName();
    this.healthService.getServicesAndLambdas().subscribe((result) => {
      this.dashboardResult = result;
      this.showTitle = true;
      this.loading = false;
    });
  }

  public openApiDocumentation(apiDocumentationUrl: string): void {
    window.open(apiDocumentationUrl, "_blank");
  }

  public onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.searchTerm = target?.value ?? "";
  }

  public clearSearch(): void {
    this.searchTerm = "";
  }

  public toggleSection(section: "services" | "lambdas" | "frontends"): void {
    switch (section) {
      case "services":
        this.servicesOpen = !this.servicesOpen;
        break;
      case "lambdas":
        this.lambdasOpen = !this.lambdasOpen;
        break;
      case "frontends":
        this.frontendsOpen = !this.frontendsOpen;
        break;
    }
  }

  public toggleSettings(): void {
    this.settingsOpen = !this.settingsOpen;
  }

  public toggleShowStackName(checked: boolean): void {
    this.showStackName = checked;
    localStorage.setItem(SHOW_STACK_NAME_KEY, JSON.stringify(checked));
  }

  public get allServices(): ServiceOverview[] {
    return this.dashboardResult.services ?? [];
  }

  public get allLambdas(): LambdaOverview[] {
    return this.dashboardResult.lambdas ?? [];
  }

  public get allFrontends(): FrontendOverview[] {
    return this.dashboardResult.frontends ?? [];
  }

  public get filteredServices(): ServiceOverview[] {
    return this.filterByName(
      (this.dashboardResult.services ?? []).filter((service) => !this.hiddenItems.isServiceHidden(service.name ?? ""))
    );
  }

  public get filteredLambdas(): LambdaOverview[] {
    return this.filterByName(
      (this.dashboardResult.lambdas ?? []).filter((lambda) => !this.hiddenItems.isLambdaHidden(lambda.name ?? ""))
    );
  }

  public get filteredFrontends(): FrontendOverview[] {
    return this.filterByName(
      (this.dashboardResult.frontends ?? []).filter(
        (frontend) => !this.hiddenItems.isFrontendHidden(frontend.name ?? "")
      )
    );
  }

  public get hiddenCount(): number {
    return (
      this.hiddenItems.hiddenServicesCount + this.hiddenItems.hiddenLambdasCount + this.hiddenItems.hiddenFrontendsCount
    );
  }

  private filterByName<T extends { name?: string | null }>(items: T[]): T[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return items;
    }
    return items.filter((item) => item.name?.toLowerCase().includes(term));
  }

  private loadShowStackName(): boolean {
    try {
      const raw = localStorage.getItem(SHOW_STACK_NAME_KEY);
      return raw ? JSON.parse(raw) === true : false;
    } catch {
      return false;
    }
  }
}
